/*
  NVRSD — scroll reveal.
  [data-reveal]            element animates in when it scrolls into view
                           values: "up" (default), "fade", "image"
  [data-reveal-group]      container; its [data-reveal-item] children get a cascading delay
                           data-reveal-step  ms between items (default 110)
                           data-reveal-base  ms before the first item (default 0)
  Delays are written to --reveal-delay and inherited by nested [data-reveal] elements.
*/
(() => {
  const root = document.documentElement;
  const MAX_STAGGER_STEPS = 6;

  const revealAll = () => {
    document.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
  };

  const setDelays = (scope) => {
    scope.querySelectorAll('[data-reveal-group]').forEach((group) => {
      const step = Number(group.dataset.revealStep || 110);
      const base = Number(group.dataset.revealBase || 0);
      const items = Array.from(group.querySelectorAll('[data-reveal-item]')).filter(
        (item) => item.closest('[data-reveal-group]') === group
      );
      items.forEach((item, i) => {
        item.style.setProperty('--reveal-delay', `${base + Math.min(i, MAX_STAGGER_STEPS) * step}ms`);
      });
    });
  };

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = window.Shopify && window.Shopify.designMode;

  if (prefersReduced || designMode || !('IntersectionObserver' in window)) {
    root.classList.remove('nvrsd-reveal-on');
    revealAll();
    return;
  }

  // A clipped (hidden) image has no visible area, so watch its container instead.
  const watched = new Map(); // observed element -> [elements to reveal]

  const onIntersect = (entries, obs) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      (watched.get(entry.target) || []).forEach((el) => el.classList.add('is-revealed'));
      watched.delete(entry.target);
      obs.unobserve(entry.target);
    });
  };

  const observer = new IntersectionObserver(onIntersect, { rootMargin: '0px 0px -10% 0px', threshold: 0.12 });
  // Content at the very end of the page can never cross the -10% line
  const endObserver = new IntersectionObserver(onIntersect, { threshold: 0 });

  const init = (scope = document) => {
    setDelays(scope);
    const pageEnd = document.documentElement.scrollHeight - window.innerHeight * 0.2;
    scope.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((el) => {
      const target = el.dataset.reveal === 'image' ? el.parentElement : el;
      if (!watched.has(target)) watched.set(target, []);
      watched.get(target).push(el);
      const top = target.getBoundingClientRect().top + window.scrollY;
      (top > pageEnd ? endObserver : observer).observe(target);
    });
  };

  init();
  window.nvrsdReveal = { init };

  // Sections re-rendered by the theme editor or section rendering API
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
